import { useState } from "react";
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Box,
  IconButton,
  Divider,
} from "@mui/material";
import { nanoid } from 'nanoid';

import {
  accordionBody,
  accordionHeaderText,
  dividerMargin,
  errorHeaderBox,
  errorLabelBox,
  FaChevronCircleDownIcon,
  FaChevronCircleUpIcon,
} from "../../PriorAuthWorkFlow/styles/Review";
import { TabSessionDataReAuthResponse } from "../../../models/api/TabSessionStorage";
import colors from "../../../styles/colors/colors";
import fontWeight from "../../../styles/mui/fontWeight";
import { TypographyBox } from "../../../app/common/TextLabel";
import { notVisitedReAuthTabs } from "../../../models/TabSessionStorage";
import { ListOptions } from "../../../models/Master";
import AgencyDetails from "./Review/AgencyDetails";
import DocumentUpload from "./Review/DocumentUpload";
import ICD10 from "./Review/ICD10";
import PhysicianDetails from "./Review/PhysicianDetails";
import RequestService from "./Review/RequestService";
import { MemberInfoData } from "../../../models/Patient";

export interface PropsFromState {
  tabSessionStorage: TabSessionDataReAuthResponse;
  masterSendCommunicationAuthTypeState: ListOptions[];
  files: File[];
  memberInfoDataState: MemberInfoData;
}

const Review: React.FC<PropsFromState> = ({ tabSessionStorage, masterSendCommunicationAuthTypeState, files, memberInfoDataState }) => {

  const accordionData = [
    {
      title: "Agency Details",
      content: <AgencyDetails tabSessionStorage={tabSessionStorage} masterSendCommunicationAuthTypeState={masterSendCommunicationAuthTypeState}/>,
    },
    {
      title: "Physician Details",
      content: <PhysicianDetails tabSessionStorage={tabSessionStorage} />,
    },
    { title: "ICD 10", content: <ICD10 tabSessionStorage={tabSessionStorage} /> },
    {
      title: "Service Request",
      content: <RequestService tabSessionStorage={tabSessionStorage} memberInfoDataState={memberInfoDataState} />,
    },
    {
      title: "Document Upload",
      content: <DocumentUpload tabSessionStorage={tabSessionStorage} files={files} />,
    },
  ];

  const [expanded, setExpanded] = useState<string[]>(accordionData.map((_, index) => `panel${index}`));

  const onChangeHandler = (isExpanded: boolean, index: number) => {
    return setExpanded((prev) => {
      if (!Array.isArray(prev)) prev = [];
      if (isExpanded) {
        return [...prev, `panel${index}`];
      } else {
        return prev.filter((panel: string) => panel !== `panel${index}`);
      }
    });
  };

  const isExpanded = (expanded: string[], index: number) => {
    return Array.isArray(expanded)
      ? expanded.includes(`panel${index}`)
      : expanded === `panel${index}`;
  };

  const icon = (index: number) => {
    return (
      <IconButton edge="start" disableRipple>
        {Array.isArray(expanded) && expanded.includes(`panel${index}`) ? (
          <FaChevronCircleUpIcon />
        ) : (
          <FaChevronCircleDownIcon />
        )}
      </IconButton>
    );
  };

  return (
    <Box>
      {accordionData.map((item, index) => (
        <Box key={nanoid()}>
          <Accordion
            expanded={isExpanded(expanded, index)}
            onChange={(_event, isExpanded) =>
              onChangeHandler(isExpanded, index)
            }
          >
            <AccordionSummary
              aria-controls={`panel${index}-content`}
              id={`panel${index}-header`}
            >
              {icon(index)}
              <Box sx={accordionHeaderText}>
                {TypographyBox(item.title, colors.fonts[20], fontWeight[5])}
              </Box>
            </AccordionSummary>
            <AccordionDetails sx={accordionBody}>{item.content}</AccordionDetails>
          </Accordion>
          {index < accordionData.length - 1 && (
            <Divider sx={dividerMargin} />
          )}
        </Box>
      ))}
      {notVisitedReAuthTabs(tabSessionStorage).length > 0 && (
        <Box m={2} >
          <Box
            sx={errorHeaderBox}
          >
            Please complete the following tabs:
          </Box>
          {notVisitedReAuthTabs(tabSessionStorage).map((tab) => (
            <Box
              key={nanoid()}
              sx={errorLabelBox}
            >
              {tab.label}
            </Box>
          ))}
        </Box>
      )}
    </Box>
  );
};

export default Review;